import { useState } from 'react'
import { motion } from 'framer-motion'
import { REEL_VIDEOS, EXAMPLE_VIDEO } from '../config/videos'
import ReelCard from './ReelCard'

export default function Showcase() {
  const [playingReelId, setPlayingReelId] = useState(null)
  const [exampleVideoError, setExampleVideoError] = useState(false)

  return (
    <section className="py-20 px-4 bg-gradient-to-b from-stadium-black to-gray-900">
      <div className="container mx-auto">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-4xl md:text-5xl font-black text-center mb-4"
        >
          <span className="text-white">THIS COULD BE </span>
          <span className="text-gold text-glow-gold">YOU</span>
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center text-gray-400 mb-12 text-lg"
        >
          Your match happened. Will anyone remember it?
        </motion.p>

        {/* Side-by-Side Comparison */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto"
        >
          {/* The Problem */}
          <div className="bg-gradient-to-br from-gray-900 to-stadium-black rounded-2xl p-6 md:p-8 border-2 border-red-500/30 relative overflow-hidden">
            <div className="absolute top-4 right-4 bg-red-500/20 px-3 py-1 rounded-full border border-red-500/50">
              <span className="text-red-400 text-xs font-bold">THE PROBLEM</span>
            </div>
            
            <div className="mt-8">
              <div className="text-6xl mb-4 text-center">⏱️</div>
              <h3 className="text-2xl font-black text-white mb-4 text-center">Long, Boring Video</h3>
              
              <div className="space-y-4 mt-6">
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                  <span className="text-gray-400 flex items-center gap-2">
                    <span>⏰</span>
                    <span>Duration</span>
                  </span>
                  <span className="text-red-400 font-bold">2+ Hours</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                  <span className="text-gray-400 flex items-center gap-2">
                    <span>👁️</span>
                    <span>Views</span>
                  </span>
                  <span className="text-red-400 font-bold">0 Views</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                  <span className="text-gray-400 flex items-center gap-2">
                    <span>😴</span>
                    <span>Engagement</span>
                  </span>
                  <span className="text-red-400 font-bold">People Skip</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                  <span className="text-gray-400 flex items-center gap-2">
                    <span>📱</span>
                    <span>Shares</span>
                  </span>
                  <span className="text-red-400 font-bold">0 Shares</span>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                <p className="text-red-300 text-sm text-center font-semibold">
                  ❌ No one watches long, unedited footage
                </p>
              </div>
            </div>
          </div>

          {/* The Solution */}
          <div className="bg-gradient-to-br from-pitch-green/20 via-gold/10 to-pitch-green/20 rounded-2xl p-6 md:p-8 border-2 border-pitch-green/50 relative overflow-hidden">
            <div className="absolute top-4 right-4 bg-pitch-green/30 px-3 py-1 rounded-full border border-pitch-green/70 backdrop-blur-sm">
              <span className="text-pitch-green text-xs font-black flex items-center gap-1">
                <span>🔥</span>
                <span>THE SOLUTION</span>
              </span>
            </div>
            
            <div className="mt-8">
              <div className="text-6xl mb-4 text-center">🔥</div>
              <h3 className="text-2xl font-black text-pitch-green mb-4 text-center">Viral Highlights</h3>
              
              <div className="space-y-4 mt-6">
                <div className="flex items-center justify-between p-3 bg-pitch-green/10 rounded-lg border border-pitch-green/30">
                  <span className="text-pitch-green flex items-center gap-2">
                    <span>⏰</span>
                    <span>Duration</span>
                  </span>
                  <span className="text-pitch-green font-black">30 Seconds</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-pitch-green/10 rounded-lg border border-pitch-green/30">
                  <span className="text-pitch-green flex items-center gap-2">
                    <span>👁️</span>
                    <span>Views</span>
                  </span>
                  <span className="text-pitch-green font-black">5K–50K Views</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-pitch-green/10 rounded-lg border border-pitch-green/30">
                  <span className="text-pitch-green flex items-center gap-2">
                    <span>🔥</span>
                    <span>Engagement</span>
                  </span>
                  <span className="text-pitch-green font-black">Teammates Share It</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-pitch-green/10 rounded-lg border border-pitch-green/30">
                  <span className="text-pitch-green flex items-center gap-2">
                    <span>📱</span>
                    <span>Shares</span>
                  </span>
                  <span className="text-pitch-green font-black">Hundreds of Shares</span>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-pitch-green/20 rounded-lg border border-pitch-green/50">
                <p className="text-pitch-green text-sm text-center font-black">
                  ✅ Hooked highlights that bring millions of views
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Reel Previews */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-16 flex justify-center items-start gap-5 md:gap-8 flex-wrap"
        >
          {REEL_VIDEOS.map((reel, index) => (
            <div key={reel.id} className="w-[240px] md:w-[280px] flex-shrink-0">
              <ReelCard
                reel={reel}
                index={index}
                playingReelId={playingReelId}
                onPlayRequest={setPlayingReelId}
              />
            </div>
          ))}
        </motion.div>
        
        {/* Example Video Embed Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="mt-16 max-w-md mx-auto"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-center mb-8">
            <span className="text-white">SEE IT IN </span>
            <span className="text-pitch-green text-glow">ACTION</span>
          </h3>
          
          <div className="bg-gradient-to-br from-stadium-black to-gray-900 rounded-2xl p-4 md:p-6 border border-pitch-green/20">
            <div className="aspect-[9/16] bg-gray-900 rounded-lg overflow-hidden mb-4 relative">
              {/* YouTube Embed */}
              {EXAMPLE_VIDEO.youtubeId && (
                <iframe
                  className="w-full h-full"
                  src={`https://www.youtube.com/embed/${EXAMPLE_VIDEO.youtubeId}?rel=0`}
                  title={EXAMPLE_VIDEO.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )}

              {/* Direct Video */}
              {EXAMPLE_VIDEO.videoUrl && !EXAMPLE_VIDEO.youtubeId && !exampleVideoError && (
                <video
                  className="w-full h-full object-cover"
                  controls
                  preload="metadata"
                  crossOrigin="anonymous"
                  onError={() => setExampleVideoError(true)}
                >
                  <source src={EXAMPLE_VIDEO.videoUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              )}
              {exampleVideoError && (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                  <p className="text-red-400 text-sm">Video couldn&apos;t load</p>
                </div>
              )}

              {/* Vimeo Embed */}
              {EXAMPLE_VIDEO.vimeoId && !EXAMPLE_VIDEO.youtubeId && !EXAMPLE_VIDEO.videoUrl && (
                <iframe
                  className="w-full h-full"
                  src={`https://player.vimeo.com/video/${EXAMPLE_VIDEO.vimeoId}`}
                  title={EXAMPLE_VIDEO.title}
                  frameBorder="0"
                  allow="autoplay; fullscreen"
                  allowFullScreen
                />
              )}

              {/* Placeholder (if no video configured) */}
              {!EXAMPLE_VIDEO.youtubeId && !EXAMPLE_VIDEO.videoUrl && !EXAMPLE_VIDEO.vimeoId && (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
                  <div className="text-center">
                    <div className="text-6xl mb-4">▶️</div>
                    <p className="text-pitch-green font-bold text-lg mb-2">{EXAMPLE_VIDEO.title}</p>
                    <p className="text-gray-400 text-sm mb-2">
                      Add your video in <code className="text-pitch-green">src/config/videos.js</code>
                    </p>
                    <p className="text-gray-500 text-xs mt-4">
                      Set EXAMPLE_VIDEO.youtubeId or EXAMPLE_VIDEO.videoUrl
                    </p>
                  </div>
                </div>
              )}
            </div>
            <p className="text-gray-400 text-sm text-center">
              {EXAMPLE_VIDEO.description}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
